/**
 * ImportMembersModal — 3 étapes :
 * 1. Choisir le fichier (drag & drop ou parcourir + télécharger le modèle)
 * 2. Aperçu et validation (preview backend)
 * 3. Confirmation (église cible, stratégie doublons, import)
 */
import { useState, useRef, useCallback } from "react";
import Modal from "./Modal";
import Button from "./Button";
import Select from "./Select";
import Badge from "./Badge";
import { colors, fonts, goldGradient } from "../styles/tokens";
import { importsApi, exportsApi } from "../services/api";
import useToast from "../hooks/useToast";
import useMembers from "../hooks/useMembers";

const MAX_SIZE_MB = 10;

export default function ImportMembersModal({ open, onClose, onImported }) {
  const toast = useToast();
  const { eglises, refresh } = useMembers();

  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [preview, setPreview] = useState(null);
  const [showErrors, setShowErrors] = useState(false);

  const [targetEgliseId, setTargetEgliseId] = useState("");
  const [duplicateStrategy, setDuplicateStrategy] = useState("skip");
  const [importing, setImporting] = useState(false);

  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const reset = useCallback(() => {
    setStep(1);
    setFile(null);
    setPreview(null);
    setUploadProgress(0);
    setUploading(false);
    setShowErrors(false);
    setTargetEgliseId("");
    setDuplicateStrategy("skip");
    setImporting(false);
  }, []);

  const close = () => {
    if (uploading || importing) return;
    reset();
    onClose?.();
  };

  // ─── Étape 1 : choix du fichier ───

  const handleFileSelect = async (selected) => {
    if (!selected) return;
    if (selected.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`Fichier trop volumineux (max ${MAX_SIZE_MB} MB)`);
      return;
    }
    const ext = selected.name.split(".").pop().toLowerCase();
    if (!["csv", "xlsx", "xls"].includes(ext)) {
      toast.error("Format non supporté. Utilisez .csv, .xlsx ou .xls");
      return;
    }

    setFile(selected);
    setUploading(true);
    setUploadProgress(0);

    try {
      const { data } = await importsApi.preview(selected, (e) => {
        if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
      });
      setPreview(data);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de lecture du fichier");
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.[0]) handleFileSelect(e.dataTransfer.files[0]);
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await importsApi.template();
      exportsApi.triggerDownload(res, "modele_import_membres.xlsx");
    } catch (err) {
      toast.error("Impossible de télécharger le modèle");
    }
  };

  // ─── Étape 3 : confirmation ───

  const handleConfirmImport = async () => {
    if (!targetEgliseId) {
      toast.error("Veuillez sélectionner une église cible");
      return;
    }
    if (!preview?.valid_rows?.length) {
      toast.error("Aucune ligne à importer");
      return;
    }

    setImporting(true);
    try {
      const { data } = await importsApi.confirm({
        rows: preview.valid_rows,
        eglise_id: Number(targetEgliseId),
        duplicate_strategy: duplicateStrategy,
        filename: preview.filename,
      });

      const messages = [];
      if (data.imported_count) messages.push(`${data.imported_count} ajouté(s)`);
      if (data.updated_count) messages.push(`${data.updated_count} mis à jour`);
      if (data.skipped_count) messages.push(`${data.skipped_count} ignoré(s)`);

      toast.success(`Import réussi · ${messages.join(" · ") || "0 ligne"}`);
      onImported?.(data);
      await refresh?.();
      reset();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setImporting(false);
    }
  };

  // ─── Render ───

  const titleByStep = {
    1: "Importer des membres",
    2: "Aperçu et validation",
    3: "Confirmer l'import",
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={titleByStep[step]}
      subtitle={
        step === 1 ? "Glissez votre fichier ou cliquez pour parcourir" :
        step === 2 ? `${preview?.filename} — ${preview?.total_rows} ligne(s) détectée(s)` :
        "Choisissez l'église cible et la stratégie pour les doublons"
      }
      size="lg"
      footer={
        step === 1 ? (
          <Button variant="ghost" onClick={close} disabled={uploading}>Annuler</Button>
        ) : step === 2 ? (
          <>
            <Button variant="ghost" onClick={() => { setStep(1); setFile(null); setPreview(null); }}>
              ← Choisir un autre fichier
            </Button>
            <Button
              onClick={() => setStep(3)}
              disabled={!preview?.valid_count}
            >
              Continuer →
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={() => setStep(2)} disabled={importing}>← Retour</Button>
            <Button
              onClick={handleConfirmImport}
              loading={importing}
              disabled={importing || !targetEgliseId}
            >
              Importer {preview?.valid_count} membre{preview?.valid_count > 1 ? "s" : ""}
            </Button>
          </>
        )
      }
    >
      {/* Stepper */}
      <Stepper step={step} />

      {/* ÉTAPE 1 */}
      {step === 1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? colors.gold : colors.border}`,
              borderRadius: "14px",
              padding: "48px 24px",
              textAlign: "center",
              background: dragOver ? colors.goldDim : colors.bg,
              cursor: uploading ? "wait" : "pointer",
              transition: "all 200ms",
            }}
          >
            <div style={{ fontSize: "44px", marginBottom: "10px" }}>📥</div>
            <div style={{ fontFamily: fonts.heading, fontSize: "20px", fontWeight: 600, marginBottom: "6px" }}>
              {uploading ? "Lecture en cours…" : "Glissez votre fichier ici"}
            </div>
            <div style={{ color: colors.textMuted, fontSize: "13px" }}>
              {uploading ? `${uploadProgress}%` : `ou cliquez pour parcourir · .xlsx, .csv (max ${MAX_SIZE_MB} MB)`}
            </div>
            {uploading && (
              <div style={{ marginTop: "16px", height: 6, background: colors.border, borderRadius: 999, overflow: "hidden" }}>
                <div style={{
                  height: "100%",
                  width: `${uploadProgress}%`,
                  background: goldGradient,
                  transition: "width 150ms",
                }} />
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => handleFileSelect(e.target.files?.[0])}
              style={{ display: "none" }}
            />
          </div>

          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "14px 16px",
            background: colors.card, borderRadius: "10px",
            border: `1px solid ${colors.border}`,
          }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: "14px" }}>📄 Pas de fichier ?</div>
              <div style={{ color: colors.textMuted, fontSize: "12px" }}>
                Téléchargez le modèle Excel pré-rempli avec exemples et instructions.
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={handleDownloadTemplate}>
              Télécharger le modèle
            </Button>
          </div>
        </div>
      )}

      {/* ÉTAPE 2 */}
      {step === 2 && preview && (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* Stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "12px" }}>
            <StatBox label="Détectés"  value={preview.total_rows} accent={colors.text} />
            <StatBox label="Valides"   value={preview.valid_count} accent={colors.green} prefix="✓ " />
            <StatBox label="Erreurs"   value={preview.error_count} accent={colors.rose} prefix="⚠ " />
            <StatBox label="Doublons" value={preview.duplicate_count} accent="#FB923C" prefix="◐ " />
          </div>

          {/* Aperçu tableau */}
          <div>
            <div style={{ fontSize: "13px", fontWeight: 600, color: colors.textMuted, marginBottom: "8px", letterSpacing: "0.05em", textTransform: "uppercase" }}>
              Aperçu (5 premières lignes valides)
            </div>
            <div style={{ overflowX: "auto", border: `1px solid ${colors.border}`, borderRadius: "10px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560, fontSize: "13px" }}>
                <thead>
                  <tr style={{ background: colors.surface }}>
                    {["Nom", "Prénom", "Email", "Téléphone", "Statut", "Cellule"].map((h) => (
                      <th key={h} style={{ padding: "10px 12px", textAlign: "left", color: colors.textMuted, fontWeight: 600, fontSize: "11px", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.length === 0 ? (
                    <tr><td colSpan="6" style={{ padding: "24px", textAlign: "center", color: colors.textMuted }}>Aucune ligne valide</td></tr>
                  ) : (
                    preview.preview.map((row, i) => (
                      <tr key={i} style={{ borderTop: `1px solid ${colors.border}` }}>
                        <td style={{ padding: "10px 12px", fontWeight: 500 }}>{row.nom || "—"}</td>
                        <td style={{ padding: "10px 12px" }}>{row.prenom || "—"}</td>
                        <td style={{ padding: "10px 12px", color: colors.textMuted }}>{row.email || "—"}</td>
                        <td style={{ padding: "10px 12px", color: colors.textMuted }}>{row.telephone || "—"}</td>
                        <td style={{ padding: "10px 12px" }}><Badge statut={row.statut || "nouveau"} size="sm">{row.statut || "nouveau"}</Badge></td>
                        <td style={{ padding: "10px 12px", color: colors.textMuted }}>{row.cellule_nom_lookup || "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Erreurs */}
          {preview.errors.length > 0 && (
            <div style={{ background: "rgba(224,91,106,0.08)", border: `1px solid ${colors.rose}40`, borderRadius: "10px", padding: "12px 16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ color: colors.rose, fontSize: "14px", fontWeight: 600 }}>
                  ⚠ {preview.error_count} erreur{preview.error_count > 1 ? "s" : ""} détectée{preview.error_count > 1 ? "s" : ""}
                </div>
                <Button size="sm" variant="ghost" onClick={() => setShowErrors(!showErrors)}>
                  {showErrors ? "Masquer" : "Voir les erreurs"}
                </Button>
              </div>
              {showErrors && (
                <div style={{ marginTop: "10px", maxHeight: 200, overflowY: "auto", fontSize: "12px" }}>
                  {preview.errors.map((e, i) => (
                    <div key={i} style={{ padding: "4px 0", color: colors.text }}>
                      <span style={{ color: colors.rose, fontFamily: fonts.mono }}>L{e.row}</span>
                      {e.field && <span style={{ color: colors.textMuted }}> · {e.field}</span>}
                      <span> · {e.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Doublons */}
          {preview.duplicates.length > 0 && (
            <div style={{ background: "rgba(251,146,60,0.08)", border: "1px solid rgba(251,146,60,0.4)", borderRadius: "10px", padding: "12px 16px", color: "#FB923C", fontSize: "13px" }}>
              ◐ {preview.duplicate_count} email{preview.duplicate_count > 1 ? "s" : ""} déjà présent{preview.duplicate_count > 1 ? "s" : ""} en base —
              vous pourrez choisir « Mettre à jour » ou « Ignorer » à l'étape suivante.
            </div>
          )}
        </div>
      )}

      {/* ÉTAPE 3 */}
      {step === 3 && preview && (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{
            background: colors.goldDim,
            border: `1px solid ${colors.goldBorder}`,
            borderRadius: "12px",
            padding: "20px",
          }}>
            <div style={{ fontFamily: fonts.heading, fontSize: "26px", fontWeight: 700, color: colors.gold, marginBottom: "8px" }}>
              Prêt à importer
            </div>
            <div style={{ color: colors.text, fontSize: "14px", display: "flex", flexDirection: "column", gap: "4px" }}>
              <div>✓ <strong>{preview.valid_count}</strong> membre{preview.valid_count > 1 ? "s" : ""} ser{preview.valid_count > 1 ? "ont" : "a"} traité{preview.valid_count > 1 ? "s" : ""}</div>
              {preview.error_count > 0 && (
                <div style={{ color: colors.rose }}>⚠ {preview.error_count} ligne{preview.error_count > 1 ? "s" : ""} ignorée{preview.error_count > 1 ? "s" : ""} (erreurs)</div>
              )}
              {preview.duplicate_count > 0 && (
                <div style={{ color: "#FB923C" }}>
                  ◐ {preview.duplicate_count} email{preview.duplicate_count > 1 ? "s" : ""} déjà en base
                </div>
              )}
            </div>
          </div>

          <Select
            label="Église cible"
            value={targetEgliseId}
            onChange={setTargetEgliseId}
            options={eglises.map((e) => ({
              value: String(e.id),
              label: `${e.nom}${e.ville ? ` — ${e.ville}` : ""}`,
            }))}
            required
            placeholder="Sélectionnez une église..."
          />

          {preview.duplicate_count > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <div style={{ fontSize: "13px", fontWeight: 500, color: colors.textMuted }}>
                Si l'email existe déjà :
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <ToggleChoice
                  active={duplicateStrategy === "skip"}
                  onClick={() => setDuplicateStrategy("skip")}
                  label="Ignorer"
                  desc="Garder le membre existant"
                />
                <ToggleChoice
                  active={duplicateStrategy === "update"}
                  onClick={() => setDuplicateStrategy("update")}
                  label="Mettre à jour"
                  desc="Écraser avec les nouvelles données"
                />
              </div>
            </div>
          )}

          {importing && (
            <div style={{ marginTop: "8px" }}>
              <div style={{ color: colors.textMuted, fontSize: "13px", marginBottom: "6px" }}>
                Importation en cours…
              </div>
              <div style={{ height: 6, background: colors.border, borderRadius: 999, overflow: "hidden" }}>
                <div style={{ height: "100%", width: "100%", background: goldGradient, animation: "pulse-bar 1.5s ease-in-out infinite" }} />
              </div>
            </div>
          )}

          <style>{`
            @keyframes pulse-bar { 0%, 100% { opacity: 0.5; } 50% { opacity: 1; } }
          `}</style>
        </div>
      )}
    </Modal>
  );
}

function Stepper({ step }) {
  const steps = ["Fichier", "Aperçu", "Importer"];
  return (
    <div style={{
      display: "flex", justifyContent: "center", marginBottom: "20px", gap: "8px",
    }}>
      {steps.map((label, i) => {
        const idx = i + 1;
        const active = idx === step;
        const done = idx < step;
        return (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{
              width: 28, height: 28, borderRadius: "50%",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              background: active ? goldGradient : (done ? colors.gold : colors.surface),
              color: active || done ? colors.bg : colors.textMuted,
              fontSize: "13px", fontWeight: 700,
              border: `1px solid ${active || done ? colors.gold : colors.border}`,
            }}>
              {done ? "✓" : idx}
            </div>
            <span style={{
              fontSize: "13px",
              color: active ? colors.gold : colors.textMuted,
              fontWeight: active ? 600 : 500,
            }}>
              {label}
            </span>
            {i < steps.length - 1 && (
              <span style={{ color: colors.border, marginLeft: "4px" }}>—</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StatBox({ label, value, accent, prefix = "" }) {
  return (
    <div style={{
      background: colors.surface,
      border: `1px solid ${colors.border}`,
      borderRadius: "10px",
      padding: "12px 14px",
    }}>
      <div style={{ fontSize: "12px", color: colors.textMuted, marginBottom: "4px" }}>{label}</div>
      <div style={{ fontFamily: fonts.heading, fontSize: "24px", fontWeight: 700, color: accent, lineHeight: 1.1 }}>
        {prefix}{value}
      </div>
    </div>
  );
}

function ToggleChoice({ active, onClick, label, desc }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: "12px 14px",
        border: `1px solid ${active ? colors.gold : colors.border}`,
        background: active ? colors.goldDim : colors.bg,
        borderRadius: "10px",
        textAlign: "left",
        cursor: "pointer",
        transition: "all 150ms",
      }}
    >
      <div style={{ fontSize: "14px", fontWeight: 600, color: active ? colors.gold : colors.text, marginBottom: "2px" }}>
        {label}
      </div>
      <div style={{ fontSize: "12px", color: colors.textMuted }}>{desc}</div>
    </button>
  );
}
